import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Button, DatePicker, Form, Input, Modal, Select, Space, Tag, Tooltip, message } from 'antd';
import { selectOvertimeData } from 'features/overtime/overtimeSlice';
import {
  updateOvertimePayrunMonth,
  getOvertimeByPayrunMonth,
  updateSingleOvertimePayrunMonth,
} from 'features/overtime/overtimeAction';
import PageHeader from 'components/PageHeader';
import moment from 'moment';
import { getUser } from 'utils/auth';
import ITVSearch from 'components/ITVSearch';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import ITVTable from 'components/ITVTable';
import { ContainerMain } from '../historyOTApproval';

const { confirm } = Modal;

const Container = styled.div`
  padding: 20px;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const PayrunManagementContainer = styled.div`
  margin-top: 20px;
`;

const SearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
`;

// Enum for modal modes
const MODE = {
  ADD: 'add',
  EDIT: 'edit',
  VIEW: 'view',
};

const OvertimePayrunManagement = () => {
  const { t } = useTranslation(['menu', 'action', 'overtime', 'timesheet']);
  const dispatch = useAppDispatch();
  const user = getUser() || {};
  const userId = user?.id;

  const overtimeData = useAppSelector(selectOvertimeData);
  const {
    overtimeRequestList,
    loadingOvertimeRequestList,
    overtimeByPayrunMonth,
    loadingOvertimeByPayrunMonth,
    payrunMonthUpdateLoading,
  } = overtimeData;

  // State for payrun management
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [searchText, setSearchText] = useState('');
  const [payrunMonth, setPayrunMonth] = useState<string>(moment().format('YYYY-MM'));
  const [viewMode, setViewMode] = useState<'all' | 'byPayrun'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // State for modal and request management
  const [mode, setMode] = useState('');
  const [currentItem, setCurrentItem] = useState<any>(null);
  const [sizePage, setSizePage] = useState(10);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Title for the header
  const titleHeader = t('menu:overtimePayrunManagement');

  // Fetch overtime data when component mounts or pagination changes
  useEffect(() => {
    if (userId) {
      if (viewMode === 'all') {
        // Gọi API by-payrun-month/null để lấy tất cả dữ liệu với phân trang
        dispatch(
          getOvertimeByPayrunMonth({
            payrunMonth: 'null',
            page: currentPage,
            pageSize: pageSize,
          }),
        );
      } else {
        dispatch(
          getOvertimeByPayrunMonth({
            payrunMonth,
            page: currentPage,
            pageSize: pageSize,
          }),
        );
      }
    }
  }, [dispatch, userId, viewMode, payrunMonth, currentPage, pageSize]);

  const filteredData = React.useMemo(() => {
    const data = Array.isArray(overtimeByPayrunMonth) ? overtimeByPayrunMonth : [];
    if (!searchText) return data.filter((item) => item.status === 'approved');
    return data.filter(
      (item: any) =>
        item.status === 'approved' &&
        (item?.projectCode?.toLowerCase().includes(searchText.toLowerCase()) ||
          item?.reason?.toLowerCase().includes(searchText.toLowerCase())),
    );
  }, [overtimeByPayrunMonth, searchText]);

  // Handle search text change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  // Handle search from ITVSearch component
  const search = (value: string) => {
    setSearchText(value);
  };

  // Handle view overtime request
  const handleView = (record: any) => {
    setCurrentItem(record);
    setMode(MODE.VIEW);
  };

  // Handle open edit modal
  const handleOpenEditModal = (record: any) => {
    setCurrentItem(record);

    // Reset form first to clear any previous values
    form.resetFields();

    // Set form values after a short delay to ensure the form is ready
    setTimeout(() => {
      const payrunMonthValue = record.payrunMonth
        ? moment(record.payrunMonth, 'YYYY-MM') // Parse string to moment object
        : moment(record.startTime).isValid()
        ? moment(record.startTime) // Keep as moment object
        : moment(); // Default to current date as moment object

      form.setFieldsValue({
        employeeCode: record.employeeCode || '',
        name: record.name || '',
        payrunMonth: payrunMonthValue, // This must be a moment object
      });
    }, 0);

    setIsEditModalVisible(true);
  };

  // Handle save payrun month
  const handleSavePayrunMonth = async () => {
    try {
      const values = await form.validateFields();

      if (!currentItem) return;

      // Ensure we have a valid moment object
      const payrunMonthMoment = moment.isMoment(values.payrunMonth)
        ? values.payrunMonth.clone() // Use .clone() if you need a copy
        : moment(values.payrunMonth, 'YYYY-MM');

      if (!payrunMonthMoment.isValid()) {
        message.error('Invalid date format');
        return;
      }

      const formattedPayrunMonth = payrunMonthMoment.format('YYYY-MM');

      dispatch(
        updateSingleOvertimePayrunMonth({
          id: currentItem.id,
          payrunMonth: formattedPayrunMonth,
          callBack: () => {
            setIsEditModalVisible(false);
            // Refresh data after update
            if (viewMode === 'all') {
              dispatch(
                getOvertimeByPayrunMonth({
                  payrunMonth: 'null',
                  page: currentPage,
                  pageSize: pageSize,
                }),
              );
            } else {
              dispatch(
                getOvertimeByPayrunMonth({
                  payrunMonth,
                  page: currentPage,
                  pageSize: pageSize,
                }),
              );
            }
            message.success(t('overtime:updatePayrunMonthSuccess'));
          },
        }),
      );
    } catch (error) {
      console.error('Error updating payrun month:', error);
      message.error('Failed to update payrun month');
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    setCurrentItem(null);
    form.resetFields();
  };

  // Handle add new overtime request
  const onAdd = () => {
    setMode(MODE.ADD);
  };

  // Handle save overtime request
  const onSave = () => {
    setMode('');
    // Refresh data after save
    if (viewMode === 'all') {
      dispatch(
        getOvertimeByPayrunMonth({
          payrunMonth: 'null',
          page: currentPage,
          pageSize: pageSize,
        }),
      );
    } else {
      dispatch(
        getOvertimeByPayrunMonth({
          payrunMonth,
          page: currentPage,
          pageSize: pageSize,
        }),
      );
    }
  };

  // Handle payrun month change for the filter
  const handlePayrunMonthChange = (date: any, dateString: string) => {
    if (dateString) {
      setPayrunMonth(dateString);
      if (viewMode === 'byPayrun') {
        dispatch(
          getOvertimeByPayrunMonth({
            payrunMonth: dateString,
            page: currentPage,
            pageSize: pageSize,
          }),
        );
      }
    }
  };

  // Handle payrun month change for the modal (doesn't trigger data fetching)
  const handleModalPayrunMonthChange = (date: any) => {
    // Just update the form field, no need to fetch data or update global state
    form.setFieldsValue({ payrunMonth: date });
  };

  // Handle assigning payrun month to selected overtimes
  const handleAssignPayrunMonth = () => {
    if (selectedRowKeys.length === 0) return;

    confirm({
      title: t('overtime:assignPayrunMonthTitle'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>{t('overtime:assignPayrunMonthConfirm')}</p>
          <p>{t('overtime:selectedRecords', { count: selectedRowKeys.length })}</p>
          <p>{t('overtime:payrunMonthTo', { month: payrunMonth })}</p>
        </div>
      ),
      onOk() {
        dispatch(
          updateOvertimePayrunMonth({
            overtimeIds: selectedRowKeys,
            payrunMonth,
            callBack: () => {
              setSelectedRowKeys([]);
              if (viewMode === 'all') {
                dispatch(
                  getOvertimeByPayrunMonth({
                    payrunMonth: 'null',
                    page: currentPage,
                    pageSize: pageSize,
                  }),
                );
              } else {
                dispatch(
                  getOvertimeByPayrunMonth({
                    payrunMonth,
                    page: currentPage,
                    pageSize: pageSize,
                  }),
                );
              }
            },
          }),
        );
      },
    });
  };

  // Handle switching view mode
  const handleViewModeChange = (mode: 'all' | 'byPayrun') => {
    setViewMode(mode);
    setSelectedRowKeys([]);
    setCurrentPage(1);

    // Tự động gọi API khi chuyển chế độ xem
    if (userId) {
      if (mode === 'all') {
        dispatch(
          getOvertimeByPayrunMonth({
            payrunMonth: 'null',
            page: currentPage,
            pageSize: pageSize,
          }),
        );
      } else {
        dispatch(
          getOvertimeByPayrunMonth({
            payrunMonth,
            page: currentPage,
            pageSize: pageSize,
          }),
        );
      }
    }
  };

  // Table columns
  const COLUMNS = [
    {
      title: t('overtime:projectName'),
      dataIndex: 'projectCode',
      key: 'projectCode',
      width: 100,
      minWidth: 100,
      render: (text: string, record: any) => <span>{text || '-'}</span>,
    },
    {
      title: t('overtime:employeeCode'),
      dataIndex: 'employeeCode',
      key: 'employeeCode',
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: string, record: any) => <span>{text || '-'}</span>,
    },
    {
      title: t('overtime:name'),
      dataIndex: 'name',
      key: 'name',
      width: 100,
      minWidth: 100,
      render: (text: string, record: any) => <span>{text || '-'}</span>,
    },
    {
      title: t('overtime:reason'),
      dataIndex: 'reason',
      key: 'reason',
      width: 100,
      minWidth: 100,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: t('overtime:startTime'),
      dataIndex: 'startTime',
      key: 'startTime',
      width: 100,
      minWidth: 100,
      render: (text: string) => <span>{text ? moment(text).format('DD/MM/YYYY HH:mm') : '-'}</span>,
    },
    {
      title: t('overtime:endTime'),
      dataIndex: 'endTime',
      key: 'endTime',
      width: 100,
      minWidth: 100,
      render: (text: string) => <span>{text ? moment(text).format('DD/MM/YYYY HH:mm') : '-'}</span>,
    },
    {
      title: t('overtime:totalHour'),
      dataIndex: 'totalHour',
      key: 'totalHour',
      width: 80,
      minWidth: 80,
      align: 'center',
      render: (text: number) => <span>{text || 0}</span>,
    },
    {
      title: t('overtime:payrunMonth'),
      dataIndex: 'payrunMonth',
      key: 'payrunMonth',
      width: 130,
      minWidth: 130,
      render: (text: string, record: any) => (
        <span>
          {text ? (
            <Tag color="green">
              <CalendarOutlined /> {text}
            </Tag>
          ) : (
            <Tag color="orange">
              {moment(record.startTime).format('YYYY-MM')}
              <Tooltip title={t('overtime:defaultPayrunMonth')}>
                <span> (Default)</span>
              </Tooltip>
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: t('overtime:action'),
      key: 'action',
      width: 80,
      minWidth: 80,
      align: 'center',
      render: (text: string, record: any) => (
        <Space>
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditModal(record);
            }}
          >
            {t('action:edit')}
          </Button>
        </Space>
      ),
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys as number[]);
    },
  };

  return (
    <ContainerMain>
      <Container>
        <PageHeader title={titleHeader} />
      </Container>
      <div style={{ backgroundColor: '#fff', padding: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
          <div>
            <Space>
              <ITVSearch
                handleSearch={(value: any) => search(value)}
                placeholder={t('action:search')}
                style={{ width: 250 }}
              />
              <Button
                type={viewMode === 'all' ? 'primary' : 'default'}
                onClick={() => handleViewModeChange('all')}
              >
                {t('overtime:allOvertime')}
              </Button>
              <Button
                type={viewMode === 'byPayrun' ? 'primary' : 'default'}
                onClick={() => handleViewModeChange('byPayrun')}
              >
                {t('overtime:byPayrunMonth')}
              </Button>
            </Space>
          </div>
          <div>
            <Space>
              {viewMode === 'byPayrun' && (
                <DatePicker
                  picker="month"
                  value={payrunMonth ? moment(payrunMonth) : null}
                  onChange={handlePayrunMonthChange}
                  format="YYYY-MM"
                />
              )}
              <Button
                type="primary"
                onClick={handleAssignPayrunMonth}
                disabled={selectedRowKeys.length === 0}
                loading={payrunMonthUpdateLoading}
                icon={<CheckCircleOutlined />}
              >
                {t('overtime:assignPayrunMonth')}
              </Button>
            </Space>
          </div>
        </div>
        <ITVTable
          loading={loadingOvertimeRequestList || loadingOvertimeByPayrunMonth}
          columns={COLUMNS}
          data={filteredData}
          width={978}
          height={550}
          isRowSelect={true}
          isRowLight={true}
          rowSelection={rowSelection}
          totalIni={filteredData?.length || 0}
          totalResult={filteredData?.length || 0}
          setSelectedUser={null}
          setOffset={(page) => {
            setCurrentPage(page);
            // No need to call API here as it will be triggered by the useEffect
          }}
          offset={currentPage}
          setLimit={(size) => {
            setPageSize(size);
            setSizePage(size);
            // Reset to page 1 when changing page size
            setCurrentPage(1);
            // No need to call API here as it will be triggered by the useEffect
          }}
          limit={pageSize}
        />
      </div>

      {/* Edit Payrun Month Modal */}
      <Modal
        title={t('overtime:editPayrunMonth')}
        open={isEditModalVisible}
        onOk={handleSavePayrunMonth}
        onCancel={handleCancelEdit}
        confirmLoading={payrunMonthUpdateLoading}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            employeeCode: currentItem?.employeeCode || '',
            name: currentItem?.name || '',
            payrunMonth: currentItem?.payrunMonth
              ? moment(currentItem.payrunMonth, 'YYYY-MM')
              : moment(currentItem?.startTime),
          }}
        >
          <Form.Item name="employeeCode" label={t('overtime:employeeCode')}>
            <Input disabled />
          </Form.Item>

          <Form.Item name="name" label={t('overtime:name')}>
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="payrunMonth"
            label={t('overtime:payrunMonth')}
            rules={[{ required: true, message: t('overtime:payrunMonthRequired') }]}
          >
            <DatePicker picker="month" onChange={handleModalPayrunMonthChange} format="YYYY-MM" />
          </Form.Item>

          <div style={{ color: '#999', fontSize: '12px' }}>{t('overtime:payrunMonthHelpText')}</div>
        </Form>
      </Modal>
    </ContainerMain>
  );
};

export default OvertimePayrunManagement;
